import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Gift, Loader2, Users, Clock, BadgeCheck, TrendingUp } from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import {
  referralConfig,
  referralFunnel,
  referralListResponse,
} from '../../lib/types'
import { formatDate, formatDateOnly } from '../../lib/format'

const STATUS_STYLES = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  qualified: { label: 'Qualified', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  rewarded: { label: 'Rewarded', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  void: { label: 'Void', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
}
function StatusPill({ status }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.void
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  )
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <div className="flex items-center gap-2 text-[color:var(--color-ink-muted)]">
        <Icon size={15} />
        <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-semibold text-[color:var(--color-ink-strong)] tabular-nums">
        {value}
      </p>
    </div>
  )
}

const inputCls =
  'rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)]'

export function PlatformReferralsPage() {
  const qc = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['referrals', 'config'],
    queryFn: async () => referralConfig.parse((await api.get('/referrals/config')).data),
  })
  const funnelQuery = useQuery({
    queryKey: ['referrals', 'funnel'],
    queryFn: async () => referralFunnel.parse((await api.get('/referrals/funnel')).data),
  })
  const listQuery = useQuery({
    queryKey: ['referrals', 'list'],
    queryFn: async () => referralListResponse.parse((await api.get('/referrals')).data),
  })

  // Local form seeded from the stored config.
  const [form, setForm] = useState(null)
  useEffect(() => {
    if (configQuery.data && !form) setForm(configQuery.data)
  }, [configQuery.data, form])

  const saveMutation = useMutation({
    mutationFn: async (body) =>
      referralConfig.parse((await api.put('/referrals/config', body)).data),
    onSuccess: (next) => {
      setForm(next)
      qc.setQueryData(['referrals', 'config'], next)
      // Enabling the programme can settle the qualified backlog → refresh
      // the funnel + list so newly-issued rewards show immediately.
      qc.invalidateQueries({ queryKey: ['referrals', 'funnel'] })
      qc.invalidateQueries({ queryKey: ['referrals', 'list'] })
      toast.success('Referral programme updated successfully.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const onSave = (e) => {
    e.preventDefault()
    if (!form) return
    saveMutation.mutate({
      enabled: form.enabled,
      extensionDays: Number(form.extensionDays),
    })
  }

  const f = funnelQuery.data
  const referralRows = listQuery.data?.data ?? []

  const columns = useMemo(
    () => [
      {
        key: 'referrer',
        header: 'Referrer',
        pinnable: true,
        minWidth: 160,
        sortAccessor: (r) => (r.referrer ?? '').toLowerCase(),
        searchAccessor: (r) => r.referrer ?? '',
        exportValue: (r) => r.referrer ?? '',
        cellClassName: 'text-[color:var(--color-ink-strong)]',
        render: (r) => r.referrer,
      },
      {
        key: 'referee',
        header: 'Referred Company',
        grow: true,
        minWidth: 200,
        sortAccessor: (r) => (r.referee ?? '').toLowerCase(),
        searchAccessor: (r) => `${r.referee ?? ''} ${r.refereeEmail ?? ''}`,
        exportValue: (r) =>
          r.refereeEmail ? `${r.referee ?? ''} <${r.refereeEmail}>` : r.referee ?? '',
        render: (r) => (
          <div className="min-w-0">
            <p className="text-[color:var(--color-ink-strong)] truncate">
              {r.referee}
            </p>
            {r.refereeEmail ? (
              <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
                {r.refereeEmail}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => STATUS_STYLES[r.status]?.label ?? r.status ?? '',
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: 'reward',
        header: 'Reward',
        minWidth: 100,
        sortAccessor: (r) => Number(r.reward?.days ?? 0),
        exportValue: (r) => (r.reward ? `+${r.reward.days} days` : ''),
        cellClassName: 'text-[color:var(--color-ink)] tabular-nums',
        render: (r) => (r.reward ? `+${r.reward.days} days` : '—'),
      },
      {
        key: 'createdAt',
        header: 'Date',
        minWidth: 130,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) =>
          formatDateOnly(r.createdAt),
        cellClassName:
          'text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (r) =>
          formatDate(r.createdAt),
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb current="Referrals" homePath="/super-admin/dashboard" />

      <header className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Gift size={20} className="text-[color:var(--color-accent-strong)]" />
          Referrals
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Configure the referral programme and track its funnel across every organisation.
        </p>
      </header>

      {/* Funnel */}
      {funnelQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 mb-6">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </p>
      ) : f ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 mb-8">
          <Stat icon={Users} label="Sign-ups" value={f.signups} />
          <Stat icon={Clock} label="Pending" value={f.pending} />
          <Stat icon={BadgeCheck} label="Qualified" value={f.qualified} />
          <Stat icon={Gift} label="Rewarded" value={f.rewarded} />
          <Stat icon={TrendingUp} label="Conversion" value={`${f.conversionRate}%`} />
        </div>
      ) : null}

      {/* Config */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 mb-8 max-w-2xl">
        <h2 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mb-4">
          Programme settings
        </h2>
        {!form ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        ) : (
          <form onSubmit={onSave} className="space-y-4">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="text-sm text-[color:var(--color-ink-strong)]">
                Referral programme active
              </span>
            </label>
            <p className="text-xs text-[color:var(--color-ink-muted)] -mt-2 ml-[1.625rem]">
              When off, sign-ups still track and qualify; rewards are issued when you turn it back on.
            </p>

            <div className="flex flex-col gap-1.5 max-w-xs">
              <span className="text-xs font-medium text-[color:var(--color-ink-muted)]">
                Plan extension per referral (days)
              </span>
              <input
                type="number"
                min="1"
                step="1"
                value={form.extensionDays}
                onChange={(e) => setForm({ ...form, extensionDays: e.target.value })}
                className={inputCls}
              />
              <span className="text-xs text-[color:var(--color-ink-muted)]">
                30 = one free month. Stacks — three referrals add 90 days.
              </span>
            </div>

            <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs text-[color:var(--color-ink-muted)]">
              When a referred company starts a trial or paid subscription, the referrer&apos;s
              plan is extended by this many days — applied immediately to their renewal date.
            </div>

            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-[color:var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-primary-hover)] disabled:opacity-60 cursor-pointer"
            >
              {saveMutation.isPending && <Loader2 size={14} className="animate-spin" />}
              Save Changes
            </button>
          </form>
        )}
      </div>

      {/* List — client-side DataTable (loaded fully): search / sort /
       *  pagination / export in the browser. Read-only (no row actions). */}
      <div>
        <h2 className="text-sm font-semibold text-[color:var(--color-ink-strong)] mb-3">
          All referrals
        </h2>
        <DataTable
          tableId="platform-referrals"
          columns={columns}
          rows={referralRows}
          getRowId={(r) => r.id}
          loading={listQuery.isLoading}
          error={
            listQuery.isError ? extractApiError(listQuery.error).message : null
          }
          onRetry={() => listQuery.refetch()}
          exportBaseName="referrals"
          exportTitle="Referrals"
          searchPlaceholder="Search Referrer or Company"
          defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
          emptyState={{ icon: Gift, title: 'No referrals yet' }}
        />
      </div>
    </div>
  )
}
